import { createContext, useContext, useState, useEffect } from 'react'
import { limpiarSesion } from '../services/sesion'
import { arrancarRenovacion } from '../services/renovarSesion'
import { useEsAdminDeLaClinica } from '../services/permisosDelServidor'
import { darDeBajaEsteEquipo } from '../push/bajaPush'
// t478 · varias cuentas en el mismo dispositivo (como Gmail): entrar con una que estaba dormida no la deja dos veces, y esta
// pestaña sabe de qué cuenta es (services/api recarga si otra pestaña cambia de cuenta por debajo).
import { olvidarDormida, fijarCuentaDeEstaPestana } from '../utils/cuentasDelDispositivo'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [realUser, setRealUser] = useState(null)
  const [token, setToken] = useState(localStorage.getItem('token'))
  const [loading, setLoading] = useState(true)
  // "Ver como rol": quien prueba la aplicación (el equipo de Biango, B-1000) puede previsualizar el
  // sistema como otro rol. Se guarda para sobrevivir a un refresco, pero se limpia al cerrar sesión.
  // Solo surte efecto si la cuenta REAL puede previsualizar (`puedeVerComo`, más abajo).
  const [verComoRolState, setVerComoRolState] = useState(() => localStorage.getItem('ver_como_rol') || null)
  // "Ver como USUARIO": lo mismo pero apuntando a una persona concreta. NO es lo mismo que ver como su
  // rol — dos personas del mismo rol pueden ver cosas distintas (un permiso concedido o quitado a esa
  // persona, y sobre todo SUS AGENDAS, que es lo que decide lo que ve un colaborador). Se guarda
  // { id, nombre, rol } porque el banner tiene que poder decir a quien estas viendo sin pedir nada.
  const [verComoUsuState, setVerComoUsuState] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ver_como_usuario') || 'null') } catch (_) { return null }
  })

  useEffect(() => {
    const savedUser = localStorage.getItem('user')
    const savedToken = localStorage.getItem('token')
    if (savedUser && savedToken) {
      setRealUser(JSON.parse(savedUser))
      setToken(savedToken)
    }
    setLoading(false)
  }, [])

  // RENOVACION SILENCIOSA. El token del equipo dura horas, no un mes: mientras la aplicacion este
  // abierta se cambia solo por uno nuevo y nadie vuelve a teclear la contrasena. Si falla, no pasa
  // nada — se sigue con el que hay. Ver services/renovarSesion.js.
  const hayToken = !!token
  useEffect(() => {
    if (!hayToken) return undefined
    return arrancarRenovacion((nuevo) => setToken(nuevo))
  }, [hayToken])

  const login = (userData, tokenData) => {
    // ENTRAR ES EMPEZAR DE CERO. Si quedaba un "ver como" de la sesion anterior —porque caduco, o
    // porque se cerro el navegador estando previsualizando— la aplicacion arrancaba con el rol de
    // otra persona y rebotaba a quien acababa de entrar. Quien mete sus credenciales entra como el
    // mismo, siempre.
    limpiarSesion()
    setVerComoRolState(null)
    setVerComoUsuState(null)
    setRealUser(userData)
    setToken(tokenData)
    localStorage.setItem('user', JSON.stringify(userData))
    localStorage.setItem('token', tokenData)
    // t478 · si esta cuenta estaba dormida en este dispositivo, ahora es la activa (no se queda dos veces); y es la de esta pestaña.
    try { olvidarDormida(userData && userData.id); fijarCuentaDeEstaPestana(userData && userData.id) } catch (_) { /* sin almacén */ }
  }

  const logout = () => {
    // B-834 · este equipo deja de recibir los avisos push de quien se va (push/bajaPush). Va ANTES de limpiar la sesión:
    // la baja la pide la sesión que se cierra. No se espera: si falla, se sale igual.
    darDeBajaEsteEquipo()
    setRealUser(null)
    setToken(null)
    setVerComoRolState(null)
    setVerComoUsuState(null)
    limpiarSesion()
  }

  // Merge parcial del usuario (p. ej. quitar must_change_password tras cambiar la contraseña).
  const actualizarUser = (patch) => {
    setRealUser(prev => {
      const next = { ...(prev || {}), ...patch }
      localStorage.setItem('user', JSON.stringify(next))
      return next
    })
  }

  // ¿El usuario REAL es administrador? (No cambia al previsualizar: es quien puede entrar/salir del modo).
  //
  // SE PREGUNTA, NO SE COPIA. Aqui ponia `realUser?.rol === 'admin' || realUser?.es_superadmin ===
  // true`, la misma frase que el servidor acaba de quitar de 32 routers. Y la previsualizacion es de
  // las que mas se notan: la ruta que la sirve (`GET /api/permisos/mias?ver_como=`) exige
  // `services/permisos.esAdmin`, asi que a una cuenta de plataforma SIN secciones se le pintaba el
  // selector de "Ver como…", elegia un rol y el servidor le devolvia sus PROPIOS permisos con el
  // banner puesto: una previsualizacion que no previsualiza nada.
  //
  // VA POR EL USUARIO REAL a proposito —sin `ver_como` en la consulta—, que es lo que impide quedarse
  // atrapado dentro de un rol sin acceso: la cara de "volver a administrador" sigue saliendo aunque
  // el rol previsualizado no sea administrador de nada. Y solo se pregunta con sesion abierta: en la
  // pantalla de entrar no hay token y pedir permisos alli solo daria un 401 en la propia pantalla de
  // login. Sin previsualizacion activa, esta consulta y la de `usePermisos` son LA MISMA entrada de
  // cache, asi que no añade ni una peticion.
  const { esAdmin: esAdminReal } = useEsAdminDeLaClinica({ habilitada: !!token })
  // ── B-1000 (1-oct-2026) · «VER COMO» SOLO PARA QUIEN PRUEBA LA APLICACION ────────────────────
  //  El dueño: «El boton "Ver como" solo debe salir a los superadmin: es un boton de pruebas». Ser
  //  administradora de la clinica (`esAdminReal`) sigue haciendo falta —previsualizar solo puede
  //  RECORTAR— pero ya no basta: ademas la cuenta REAL tiene que ser la del equipo de Biango. Eso
  //  tampoco se copia aqui: lo contesta el servidor en la MISMA respuesta (`_puede_ver_como`, el
  //  criterio con el que acepta o ignora la previsualizacion) y aqui se lee. Leer
  //  `realUser.es_superadmin` del navegador seria fiarse de un dato que cualquiera puede editar y
  //  que se queda viejo. Es la misma consulta de arriba (la misma entrada de cache, ni una peticion
  //  mas); va en su propia linea para no tocar la de `esAdminReal`, que tiene su guardia.
  const { puedeVerComo: puedeProbar, listo: seSabeSiPuede } = useEsAdminDeLaClinica({ habilitada: !!token })
  const puedeVerComo = esAdminReal && puedeProbar === true
  // LO QUE QUEDO GUARDADO DE ANTES SE TIRA. Hasta B-1000 cualquier administradora podia dejar una
  // previsualizacion en su navegador (`ver_como_rol` / `ver_como_usuario`). Para ella ya no surte
  // efecto (abajo sale null y el servidor ignora la cabecera), pero dejarla ahi seguiria mandando
  // esa cabecera en cada peticion para nada. Solo cuando la respuesta HA LLEGADO: mientras esta de
  // camino tras un F5 `puedeVerComo` es false tambien para quien si puede, y borrar ahi le quitaria
  // la previsualizacion a quien esta probando.
  useEffect(() => {
    if (!seSabeSiPuede || puedeVerComo) return
    if (!verComoRolState && !verComoUsuState) return
    localStorage.removeItem('ver_como_rol')
    localStorage.removeItem('ver_como_usuario')
    setVerComoRolState(null)
    setVerComoUsuState(null)
  }, [seSabeSiPuede, puedeVerComo, verComoRolState, verComoUsuState])
  // Los dos modos son EXCLUYENTES: entrar en uno apaga el otro. Si convivieran, el menu se pintaria
  // con el rol y los permisos vendrian del usuario, y la previsualizacion mentiria.
  const setVerComoRol = (rol) => {
    if (!puedeVerComo) return
    localStorage.removeItem('ver_como_usuario'); setVerComoUsuState(null)
    if (rol) { localStorage.setItem('ver_como_rol', rol); setVerComoRolState(rol) }
    else { localStorage.removeItem('ver_como_rol'); setVerComoRolState(null) }
  }
  const setVerComoUsuario = (u) => {
    if (!puedeVerComo) return
    localStorage.removeItem('ver_como_rol'); setVerComoRolState(null)
    if (u && u.id) { localStorage.setItem('ver_como_usuario', JSON.stringify(u)); setVerComoUsuState(u) }
    else { localStorage.removeItem('ver_como_usuario'); setVerComoUsuState(null) }
  }
  // Previsualizacion EFECTIVA: null salvo que quien puede previsualizar este viendo como otro. Quien
  // deja de poder (o quien nunca pudo: la administradora de una clinica) no previsualiza nada,
  // aunque quede algo en el navegador.
  const verComoRol = puedeVerComo && verComoRolState ? verComoRolState : null
  const verComoUsuario = puedeVerComo && verComoUsuState ? verComoUsuState : null

  // Usuario EFECTIVO: al previsualizar se le cambian solo rol/base_rol (y se le quita es_superadmin)
  // para que TODO el gating visual (menús, rutas, pestañas) refleje ese rol. El backend NO cambia (su
  // JWT sigue siendo admin): es una previsualización visual, los datos siguen llegando. La cara de
  // "volver a administrador" usa puedeVerComo (la cuenta REAL), no este user, así que nunca se queda atrapado.
  const user = verComoUsuario
    ? { ...realUser, rol: verComoUsuario.rol, base_rol: verComoUsuario.rol, es_superadmin: false,
        _ver_como: verComoUsuario.rol, _ver_como_usuario: verComoUsuario.id }
    : verComoRol
      ? { ...realUser, rol: verComoRol, base_rol: verComoRol, es_superadmin: false, _ver_como: verComoRol }
      : realUser

  return (
    <AuthContext.Provider value={{ user, realUser, token, login, logout, loading, actualizarUser, esAdminReal, verComoRol, setVerComoRol, verComoUsuario, setVerComoUsuario, puedeVerComo }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
